import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2 } from "lucide-react";
import BrandLogo from "../components/BrandLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showRecovery, setShowRecovery] = useState(false);

  useEffect(() => {
    // Auto-redirect to dashboard ONLY when we have a real Bearer token
    // in localStorage (set by either password login or Google SSO).
    // Relying on cookies alone caused an infinite loop on production:
    // a stale cookie would push the user back to /admin/dashboard, the
    // dashboard would fail (no matching Bearer), redirect back here,
    // cookie still valid → loop. Validate with the Bearer header so the
    // server's reply actually reflects whether THIS tab can reach the API.
    const token = localStorage.getItem("lf_session");
    if (!token) return;
    axios
      .get(`${API}/auth/me`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => navigate("/admin/dashboard", { replace: true }))
      .catch((e) => {
        // Token exists but was rejected — wipe it so subsequent attempts
        // (e.g. Google SSO) start from a clean state instead of looping.
        if (e?.response?.status === 401 || e?.response?.status === 403) {
          localStorage.removeItem("lf_session");
        }
      });
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const res = await axios.post(`${API}/auth/admin/login`, { email, password }, { withCredentials: true });
      localStorage.setItem("lf_session", res.data.session_token);
      navigate("/admin/dashboard", { state: { user: res.data.user } });
    } catch (e) {
      setError(e.response?.data?.detail || "Invalid credentials. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <BrandLogo variant="mark" size={56} />
          </div>
          <h1 className="text-2xl font-extrabold text-zinc-950">Admin Login</h1>
          <p className="text-zinc-500 text-sm mt-1">FastPrototype.ai Admin Panel</p>
        </div>
        <div className="bg-white border border-zinc-200 rounded-2xl p-8 shadow-sm">
          <button data-testid="btn-google-login" onClick={handleGoogleLogin}
            className="w-full flex items-center justify-center gap-3 py-3 border border-zinc-200 rounded-xl text-sm font-semibold text-zinc-700 hover:bg-zinc-50 transition-colors mb-5">
            <svg width="18" height="18" viewBox="0 0 18 18"><path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 002.38-5.88c0-.57-.05-.66-.15-1.18z"/><path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 01-7.18-2.54H1.83v2.07A8 8 0 008.98 17z"/><path fill="#FBBC05" d="M4.5 10.52a4.8 4.8 0 010-3.04V5.41H1.83a8 8 0 000 7.18l2.67-2.07z"/><path fill="#EA4335" d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 001.83 5.4L4.5 7.49a4.77 4.77 0 014.48-3.3z"/></svg>
            Continue with Google
          </button>
          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px bg-zinc-200" />
            <span className="text-xs text-zinc-400 font-medium">or</span>
            <div className="flex-1 h-px bg-zinc-200" />
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-zinc-700 mb-1.5">Email</label>
              <input data-testid="input-admin-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required
                className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-zinc-900 focus:outline-none text-sm" placeholder="admin@fastprototype.ai" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-zinc-700 mb-1.5">Password</label>
              <input data-testid="input-admin-password" type="password" value={password} onChange={e => setPassword(e.target.value)} required
                className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-zinc-900 focus:outline-none text-sm" placeholder="••••••••" />
            </div>
            {error && <div data-testid="login-error" className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">{error}</div>}
            <button data-testid="btn-login-submit" type="submit" disabled={loading}
              className="w-full py-3 bg-zinc-950 text-white rounded-full text-sm font-semibold hover:bg-zinc-800 disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
              {loading ? <><Loader2 size={15} className="animate-spin" /> Signing in...</> : "Sign In"}
            </button>
          </form>
          <div className="mt-6 pt-6 border-t border-zinc-100 text-center">
            <button
              data-testid="btn-open-recovery-help"
              onClick={() => setShowRecovery(true)}
              className="text-xs text-zinc-500 hover:text-zinc-800 underline underline-offset-2"
            >
              Locked out? Emergency recovery
            </button>
          </div>
        </div>
      </div>
      {showRecovery && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowRecovery(false)} data-testid="recovery-modal-overlay">
          <div className="bg-white rounded-2xl max-w-xl w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="recovery-modal">
            <h3 className="font-bold text-zinc-950 text-lg mb-2">Emergency admin recovery</h3>
            <p className="text-sm text-zinc-700 leading-relaxed mb-4">
              If you're locked out of the admin panel (no working password, no Google SSO), you can reset
              the default admin account by proving you own the deployment — run this once from any shell
              with access to the server's <code className="text-xs bg-zinc-100 px-1 py-0.5 rounded">JWT_SECRET_KEY</code>:
            </p>
            <pre className="text-[11px] bg-zinc-950 text-emerald-300 p-4 rounded-xl overflow-x-auto whitespace-pre leading-relaxed font-mono" data-testid="recovery-curl-snippet">{`curl -X POST ${window.location.origin}/api/auth/admin/emergency-reseed \\
  -H "Content-Type: application/json" \\
  -d '{
    "emergency_token": "<your JWT_SECRET_KEY env var>",
    "email": "admin@launchfast.io",
    "password": "YourNewStrongPassword!"
  }'`}</pre>
            <p className="text-xs text-zinc-500 leading-relaxed mt-4">
              After it returns <code className="bg-zinc-100 px-1 rounded">{"{ok: true}"}</code>, come back to this
              screen and sign in with the password you just set. The call is logged to the audit log so the
              reset is always traceable.
            </p>
            <p className="text-xs text-zinc-500 leading-relaxed mt-3">
              Alternative: set <code className="bg-zinc-100 px-1 rounded">FORCE_RESEED_DEFAULT_ADMIN=1</code> in
              your backend env and restart — the next boot will reset{" "}
              <code className="bg-zinc-100 px-1 rounded">admin@launchfast.io</code>'s password back to the
              original default <code className="bg-zinc-100 px-1 rounded">LaunchFast2024!</code>. Remove the
              env var afterwards.
            </p>
            <div className="flex justify-end mt-5">
              <button
                data-testid="btn-close-recovery"
                onClick={() => setShowRecovery(false)}
                className="px-4 py-2 bg-zinc-950 text-white text-sm font-semibold rounded-full hover:bg-zinc-800"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
